import * as Sentry from "@sentry/vue";
import Swal from "sweetalert2";

const MANUAL_REPORT_SHORTCUT = "Ctrl + Alt + B";

let sentryEnabled = false;
let manualReportInProgress = false;

function getReplayNetworkUrls() {
  return [
    window.location.origin,
    process.env.VUE_APP_COMUNICANTE_API,
    process.env.VUE_APP_COMUNICANTE_API_PAINEL,
    process.env.VUE_APP_VALORA_API
  ].filter((url, index, urls) => url && urls.indexOf(url) === index);
}

async function copyText(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const input = document.createElement("textarea");
  input.value = text;
  input.setAttribute("readonly", "");
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  input.select();
  document.execCommand("copy");
  document.body.removeChild(input);
}

function getCurrentUserContext() {
  try {
    const user = JSON.parse(localStorage.getItem("usuario"));

    if (!user) return null;

    return {
      id: String(user.id || user.usuario_id || user.login || "unknown"),
      accountId: user.conta_id || user.contaId || null,
      clientId: user.cliente_id || user.clienteId || null
    };
  } catch (error) {
    return null;
  }
}

function addUserContextToEvent(event) {
  const user = getCurrentUserContext();

  if (!user) return event;

  event.user = { ...event.user, id: user.id };
  event.tags = {
    ...event.tags,
    conta_id: user.accountId || "sem-conta",
    cliente_id: user.clientId || "sem-cliente"
  };

  return event;
}

export function initSentry(Vue, router) {
  const dsn = process.env.VUE_APP_SENTRY_DSN;

  if (!dsn) {
    console.warn(
      "Sentry desativado: configure VUE_APP_SENTRY_DSN para habilitar os relatos de bug."
    );
    return false;
  }

  Sentry.init({
    Vue,
    dsn,
    environment: process.env.VUE_APP_NODE_ENV || process.env.NODE_ENV,
    attachProps: false,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        // Inclui payloads e cabecalhos uteis das APIs, mas nunca captura
        // endpoints que normalmente transportam credenciais ou codigos.
        networkDetailAllowUrls: getReplayNetworkUrls(),
        networkDetailDenyUrls: [
          /login|senha|password|token|oauth|2fa|codigo-acao-sensivel|desbloquear-tela/i
        ],
        networkCaptureBodies: true,
        networkRequestHeaders: [
          "Accept",
          "Content-Type",
          "X-Requested-With",
          "X-Presentation-Mode"
        ],
        networkResponseHeaders: [
          "Content-Type",
          "Content-Length",
          "X-Request-Id",
          "X-Correlation-Id",
          "Server-Timing"
        ]
      })
    ],
    // Mantem a sessao completa para que o relato manual tenha pelo menos o
    // minuto anterior. O buffer de erro isolado pode conter apenas os segundos
    // posteriores ao ultimo snapshot interno do SDK.
    replaysSessionSampleRate: 1,
    // Tambem preserva o Replay quando ocorrer um erro automatico.
    replaysOnErrorSampleRate: 1,
    tracesSampleRate: 0,
    // O SDK aceita no maximo 100 breadcrumbs no evento. O Replay mantem os
    // logs de console e os detalhes de rede ao longo da gravacao preservada.
    maxBreadcrumbs: 100,
    beforeSend: addUserContextToEvent
  });

  router.afterEach((to, from) => {
    Sentry.addBreadcrumb({
      category: "navigation",
      type: "navigation",
      level: "info",
      message: `${from.fullPath || "inicio"} -> ${to.fullPath}`,
      data: {
        from: from.name || from.path || null,
        to: to.name || to.path || null
      }
    });
  });

  sentryEnabled = true;
  return true;
}

export async function captureManualBugReport(
  title,
  description,
  router,
  reportStartedAt
) {
  if (!sentryEnabled) {
    throw new Error("Sentry nao esta configurado neste ambiente.");
  }

  const replay = Sentry.getReplay();
  const route = router.currentRoute;
  const reportTitle = title.trim();
  const reportTimestamp = reportStartedAt || new Date();
  const reportContext = {
    title: reportTitle,
    description: description || "Sem descricao informada",
    routeName: route.name || null,
    routePath: route.fullPath,
    url: window.location.href,
    online: navigator.onLine,
    language: navigator.language,
    userAgent: navigator.userAgent,
    timestamp: reportTimestamp.toISOString(),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: `${window.screen.width}x${window.screen.height}`,
    replayId: replay ? replay.getReplayId() : null
  };

  Sentry.addBreadcrumb({
    category: "manual-report",
    level: "warning",
    message: "Usuario acionou o relato manual de bug",
    data: reportContext,
    timestamp: reportTimestamp.getTime() / 1000
  });

  const eventId = Sentry.captureEvent({
    message: reportTitle,
    level: "warning",
    tags: { origem: "atalho-manual" },
    fingerprint: ["relato-manual", reportTitle],
    contexts: { relato_manual: reportContext },
    // O envio ocorre depois do preenchimento, mas o erro aconteceu quando o
    // atalho foi acionado. Isso posiciona o player antes da abertura do modal.
    timestamp: reportTimestamp.getTime() / 1000
  });

  // Aguarda brevemente o envio do evento antes de confirmar para o usuario.
  await Sentry.flush(5000);

  return {
    eventId,
    replayId: replay ? replay.getReplayId() : null
  };
}

export function installManualBugReportShortcut(router) {
  window.addEventListener("keydown", async (event) => {
    const shortcutPressed =
      event.ctrlKey && event.altKey && event.key.toLowerCase() === "b";

    if (!shortcutPressed || manualReportInProgress) return;

    event.preventDefault();
    manualReportInProgress = true;

    try {
      const reportStartedAt = new Date();
      const replay = Sentry.getReplay();

      // Envia tudo o que foi gravado ate o instante do atalho antes de abrir o
      // modal. O evento usa este mesmo instante para posicionar o player.
      if (replay) await replay.flush();

      const result = await Swal.fire({
        title: "Relatar bug",
        customClass: {
          popup: "sentry-manual-report"
        },
        html: `
          <div class="sentry-manual-report__form">
            <p>Foi preservado o minuto anterior ao relato. Atalho: ${MANUAL_REPORT_SHORTCUT}.</p>
            <label for="sentry-report-title">Título</label>
            <input id="sentry-report-title" class="swal2-input" maxlength="150"
              placeholder="Ex.: Erro ao salvar campanha" aria-label="Título do bug">
            <label for="sentry-report-description">Descrição</label>
            <textarea id="sentry-report-description" class="swal2-textarea"
              placeholder="O que aconteceu e o que você esperava que acontecesse?"
              aria-label="Descrição do bug"></textarea>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: "Enviar diagnóstico",
        cancelButtonText: "Cancelar",
        showLoaderOnConfirm: true,
        focusConfirm: false,
        onOpen: () => {
          document.getElementById("sentry-report-title").focus();
        },
        allowOutsideClick: () => !Swal.isLoading(),
        preConfirm: async () => {
          const title = document
            .getElementById("sentry-report-title")
            .value.trim();
          const description = document
            .getElementById("sentry-report-description")
            .value.trim();

          if (!title) {
            Swal.showValidationMessage("Informe um título para o problema.");
            return false;
          }

          try {
            return await captureManualBugReport(
              title,
              description,
              router,
              reportStartedAt
            );
          } catch (error) {
            Swal.showValidationMessage(
              `Não foi possível enviar: ${error.message}`
            );
            return false;
          }
        }
      });

      if (result.value) {
        const copyResult = await Swal.fire({
          icon: "success",
          title: "Diagnóstico enviado",
          text: "Copie o código gerado e compartilhe com o suporte para que ele encontre o diagnóstico.",
          input: "text",
          inputValue: result.value.eventId,
          inputAttributes: {
            readonly: "readonly",
            "aria-label": "Código do diagnóstico"
          },
          confirmButtonText: "Copiar código",
          showCancelButton: true,
          cancelButtonText: "Fechar"
        });

        if (copyResult.value) {
          await copyText(result.value.eventId);
          await Swal.fire({
            icon: "success",
            title: "Código copiado",
            text: "Agora você pode compartilhar o código com o suporte.",
            timer: 2500,
            showConfirmButton: false
          });
        }
      }
    } finally {
      manualReportInProgress = false;
    }
  });
}
